import { useMemo, useEffect } from 'react';
import { useTheme } from '@simplywallst/ui-core';
import { toCurrencyTruncated, toPercentage } from '@/utilities/formatting';
import { format } from 'date-fns';
import TimeSeries, {
  Line,
  Area,
  DataTable,
  Title,
  Value,
} from '@/components/TimeSeries';
import { useTimeSeriesReducer } from '@/components/TimeSeries/hooks';
import { useChartDefinitionsContext } from '@/components/ChartDefinitionsContext';
import { useTracking } from 'react-tracking';

enum Series {
  series01 = 'debt',
  series02 = 'equity',
  series03 = 'cash and equivalents',
}

type DefaultFilter = {
  [key in Series]: boolean;
};

export interface Props {
  cash: number[][];
  cashMax: number;
  cashMin: number;
  currencyISO: string;
  debt: number[][];
  debtMax: number;
  debtMin: number;
  equity: number[][];
  equityMax: number;
  equityMin: number;
  reportingUnit: number;
  defaultFilter?: DefaultFilter;
  hideToggleFilter?: boolean;
  labels?: {
    seriesLabels: readonly [string, string, string];
    tooltipSuffix: string;
    noData: string;
  };
}

export const DebtToEquityHistory = ({
  cash,
  cashMax,
  cashMin,
  currencyISO,
  debt,
  debtMax,
  debtMin,
  equity,
  equityMax,
  equityMin,
  reportingUnit,
  defaultFilter,
  hideToggleFilter,
  labels = {
    seriesLabels: ['Debt', 'Equity', 'Cash and Equivalent'],
    tooltipSuffix: 'Debt/Equity Ratio',
    noData: 'n/a',
  },
}: Props) => {
  const theme = useTheme();
  const { trackEvent, Track } = useTracking({
    type: 'track',
    subject: 'chart',
    modifier: 'DebtToEquityHistory',
  });
  const { chart01Gradient01, chart02Gradient01, chartNEGGradient01 } =
    useChartDefinitionsContext();

  const data = useMemo(
    () => ({
      [Series.series01]: debt,
      [Series.series02]: equity,
      [Series.series03]: cash,
    }),
    [debt, equity, cash]
  );

  const minMaxY = {
    [Series.series01]: [debtMin, debtMax],
    [Series.series02]: [equityMin, equityMax],
    [Series.series03]: [cashMin, cashMax],
  };

  const {
    entities,
    keys,
    minimumX,
    maximumX,
    periods,
    series,
    filter,
    disabled,
    dispatch,
  } = useTimeSeriesReducer<Series>({
    data,
    defaultFilter: defaultFilter as DefaultFilter,
    hideToggleFilter,
  });

  useEffect(
    () =>
      dispatch({
        type: 'reset',
        payload: {
          data,
          defaultFilter: defaultFilter as DefaultFilter,
        },
      }),
    [dispatch, data]
  );

  const colors = {
    [Series.series01]: theme.color.bad,
    [Series.series02]: theme.color.chart01,
    [Series.series03]: theme.color.chart02,
  };

  const gradients = {
    [Series.series01]: {
      fillPositive: chartNEGGradient01,
      fillNegative: chartNEGGradient01,
    },
    [Series.series02]: {
      fillPositive: chart01Gradient01,
      fillNegative: chartNEGGradient01,
    },
    [Series.series03]: {
      fillPositive: chart02Gradient01,
      fillNegative: chartNEGGradient01,
    },
  };

  const minimumY = Math.min(
    ...series.filter((n) => filter[n]).map((n) => minMaxY[n][0])
  );
  const maximumY = Math.max(
    ...series.filter((n) => filter[n]).map((n) => minMaxY[n][1])
  );

  const { seriesLabels } = labels;

  const translatedSeries = useMemo(
    () => [
      {
        key: Series.series01,
        label: seriesLabels[0],
      },
      {
        key: Series.series02,
        label: seriesLabels[1],
      },
      {
        key: Series.series03,
        label: seriesLabels[2],
      },
    ],
    [seriesLabels]
  );

  return (
    <Track>
      <TimeSeries<Series>
        entities={entities}
        keys={keys}
        filter={filter}
        disabled={disabled}
        maximumX={maximumX}
        maximumY={maximumY}
        minimumX={minimumX}
        minimumY={minimumY > 0 ? 0 : minimumY}
        periods={periods}
        series={translatedSeries}
        hasTrailingYear={false}
        seriesColorSelect={(series) => colors[series]}
        formatYAxis={(value) =>
          toCurrencyTruncated(value * reportingUnit, 0, currencyISO)
        }
        onLegendClick={(series) => {
          trackEvent({
            legend: series,
            action: 'click',
            isVisible: !filter[series],
          });
          dispatch({
            type: 'filter',
            payload: {
              key: series,
              data,
            },
          });
        }}
        dataTable={({ key }) => {
          const entity = entities[key];
          const debt = entity ? entity[Series.series01] : undefined;
          const equity = entity ? entity[Series.series02] : undefined;
          const margin =
            debt === undefined || equity === undefined
              ? undefined
              : debt / equity;
          return (
            <DataTable noWrap>
              <thead>
                <tr>
                  <th colSpan={2}>
                    {key ? format(key, 'MMM dd yyyy') : `n/a`}
                  </th>
                </tr>
              </thead>
              <tbody>
                {series
                  .filter((n) => filter[n])
                  .map((key, index) => {
                    const value =
                      entity !== undefined && entity[key] !== undefined
                        ? entity[key] * reportingUnit
                        : undefined;
                    return (
                      <tr key={`data-table-row-${key}`}>
                        <Title
                          data-cy-id={`chart-debt-to-equity-history-datatable-title-${key}`}
                        >
                          {labels.seriesLabels[index]}
                        </Title>
                        <Value>
                          <Value
                            as="div"
                            data-cy-id={`chart-debt-to-equity-history-datatable-value-${key}`}
                            noData={value === undefined}
                            colorPos={colors[key]}
                            colorNeg={theme.color.bad}
                            value={value}
                          >
                            {value !== undefined &&
                              toCurrencyTruncated(value, 3, currencyISO)}
                          </Value>
                          {key === Series.series02 && (
                            <Value
                              as="div"
                              data-cy-id={`chart-debt-to-equity-history-datatable-debt-equity-ratio-${key}`}
                              colorPos={theme.color.white}
                              colorNeg={theme.color.bad}
                              postfix={` ${labels.tooltipSuffix}`}
                              value={margin || 0}
                            >
                              {margin !== undefined
                                ? toPercentage(margin, 1)
                                : labels.noData}
                            </Value>
                          )}
                        </Value>
                      </tr>
                    );
                  })}
              </tbody>
            </DataTable>
          );
        }}
      >
        {({ xScale, yScale, keys, entities }) =>
          series
            .filter((n) => filter[n])
            .map((key) => {
              return (
                <g key={`chart-${key}`}>
                  <Area
                    data={keys.filter((d) => entities[d][key] !== undefined)}
                    xSelect={(d) => d}
                    ySelect={(d) => entities[d][key]}
                    xScale={xScale}
                    yScale={yScale}
                    fillPos={gradients[key].fillPositive}
                    fillNeg={gradients[key].fillNegative}
                  />
                  <Line
                    data={keys.filter((d) => entities[d][key] !== undefined)}
                    xSelect={(d) => d}
                    ySelect={(d) => entities[d][key]}
                    xScale={xScale}
                    yScale={yScale}
                    strokePos={colors[key]}
                  />
                </g>
              );
            })
        }
      </TimeSeries>
    </Track>
  );
};

DebtToEquityHistory.defaultProps = {
  defaultFilter: {
    [Series.series01]: true,
    [Series.series02]: true,
    [Series.series03]: false,
  },
  hideToggleFilter: false,
};
